import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { get, postJson, putJson, del } from '../../../hook/request';
import { toast } from 'react-toastify';

type UserEmailRow = {
  ID: number;
  categoryID: number;
  email: string;
  password: string;
};

type CategoryApi = {
  ID: number;
  meta?: unknown;
  codice?: string;
  ordine?: number;
  abilitato?: 0 | 1;
};

type CategoryOption = { id: number; label: string };

function parseMetaName(meta: unknown, lang = 'it'): string {
  if (!meta) return '';
  try {
    const obj = typeof meta === 'string' ? JSON.parse(meta) : (meta as any);
    const byLang = obj?.[lang] || {};
    return (byLang.email ?? '').toString();
  } catch {
    return '';
  }
}

type Props = { clienteID: string };

const Emails: React.FC<Props> = ({ clienteID }) => {
  const [open, setOpen] = useState<'usersEmail' | ''>('usersEmail');
  const toggle = (id: 'usersEmail') => setOpen(prev => (prev === id ? '' : id));

  const [loading, setLoading] = useState(true);
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [deletingRow, setDeletingRow] = useState<number | null>(null);

  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [rows, setRows] = useState<UserEmailRow[]>([]);

  const [newCategory, setNewCategory] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');

  const categoryMap = useMemo(
    () => new Map(categories.map(c => [c.id, c.label])),
    [categories]
  );

  const normalizeRows = (raw: any[]): UserEmailRow[] =>
    raw.map(r => ({
      ID: Number(r.ID ?? r.id ?? 0),
      categoryID: Number(r.categoryID ?? r.typeID ?? r.tipoID ?? 0),
      email: String(r.email ?? r.nominativo ?? ''),
      password: String(r.password ?? ''),
    }));

  const loadAll = async () => {
    try {
      // categorie
      const catRes = await get<CategoryApi[] | { data?: CategoryApi[] }>(
        `/categories/parent?ID=684`
      );
      const catRoot: any = (catRes as any)?.data ?? catRes;
      const catArr: CategoryApi[] = Array.isArray(catRoot)
        ? catRoot
        : Array.isArray(catRoot?.data)
        ? catRoot.data
        : [];

      const catOpts: CategoryOption[] = catArr
        .filter(c => (c.abilitato ?? 1) === 1)
        .sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0))
        .map(c => ({
          id: Number(c.ID),
          label: parseMetaName(c.meta, 'it') || c.codice || String(c.ID),
        }));

      setCategories(catOpts);

      // lista users/email
      // NB: tengo clienteID nella query come nel tuo componente originale
      const res = await get<UserEmailRow[] | { data?: UserEmailRow[] }>(
        `/users/email?clienteID=${encodeURIComponent(clienteID)}`
      );
      const root: any = (res as any)?.data ?? res;
      const arr: any[] = Array.isArray(root)
        ? root
        : Array.isArray(root?.data)
        ? root.data
        : [];

      setRows(normalizeRows(arr));
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Errore nel caricamento');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadAll();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetNew = () => {
    setNewCategory('');
    setNewEmail('');
    setNewPassword('');
  };

  const reloadRows = async () => {
    const res = await get<UserEmailRow[] | { data?: UserEmailRow[] }>(
      `/users/email?clienteID=${encodeURIComponent(clienteID)}`
    );
    const root: any = (res as any)?.data ?? res;
    const arr: any[] = Array.isArray(root)
      ? root
      : Array.isArray(root?.data)
      ? root.data
      : [];
    setRows(normalizeRows(arr));
  };

  const handleAdd = async () => {
    if (!newCategory || !newEmail || !newPassword) {
      toast.warn('Seleziona una categoria e inserisci email e password');
      return;
    }

    const payload = {
      clienteID,
      categoryID: Number(newCategory),
      email: newEmail.trim(),
      password: newPassword, // non trimmo la password per evitare alterazioni
      user_email: localStorage.getItem('email') || 'supporto@i-factory.biz',
    };

    try {
      setSavingRow(-1);
      await postJson(`/users/email`, payload);
      toast.success('Utente email aggiunto');
      await reloadRows();
      resetNew();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? e?.message ?? 'Errore aggiunta'
      );
    } finally {
      setSavingRow(null);
    }
  };

  const handleUpdate = async (row: UserEmailRow) => {
    const payload = {
      id: row.ID,
      clienteID,
      categoryID: row.categoryID,
      email: row.email.trim(),
      password: row.password,
      user_email: localStorage.getItem('email') || 'supporto@i-factory.biz',
    };

    try {
      setSavingRow(row.ID);
      await putJson(`/users/email`, payload);
      toast.success('Utente email aggiornato');
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? e?.message ?? 'Errore aggiornamento'
      );
    } finally {
      setSavingRow(null);
    }
  };

  const handleDelete = async (row: UserEmailRow) => {
    if (!window.confirm('Eliminare questo utente email?')) return;

    try {
      setDeletingRow(row.ID);
      await del(`/users/email?ID=${encodeURIComponent(String(row.ID))}`);
      setRows(prev => prev.filter(r => r.ID !== row.ID));
      toast.success('Utente email eliminato');
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? e?.message ?? 'Errore eliminazione'
      );
    } finally {
      setDeletingRow(null);
    }
  };

  if (loading) {
    return (
      <CardLike>
        <div className="d-flex justify-content-center py-5">
          <Spinner />
        </div>
      </CardLike>
    );
  }

  return (
    <Accordion
      open={open}
      toggle={() => toggle('usersEmail')}
      className="icons-accordion w-100"
    >
      <AccordionItem>
        <AccordionHeader
          targetId={'usersEmail'}
          className="gap-2 text-secondary bg-light-secondary"
        >
          <span className="font-secondary">Users Email</span>
          {open === 'usersEmail' ? (
            <i className="iconly-Arrow-Up-2 icli ms-auto icon font-secondary" />
          ) : (
            <i className="iconly-Arrow-Down-2 icli ms-auto icon font-secondary" />
          )}
        </AccordionHeader>

        <AccordionBody accordionId={'usersEmail'}>
          {/* Riga di inserimento */}
          <Row className="g-3 align-items-end mb-3">
            <Col md={3}>
              <Label>Categoria</Label>
              <Input
                type="select"
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
              >
                <option value="">Seleziona…</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </Input>
            </Col>

            <Col md={4}>
              <Label>Email</Label>
              <Input
                type="text"
                placeholder="Email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
              />
            </Col>

            <Col md={3}>
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="Password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
            </Col>

            <Col md={2} className="d-flex  mt-2">
              <Btn
                color="success"
                className="text-white"
                onClick={handleAdd}
                disabled={savingRow === -1}
              >
                Aggiungi
              </Btn>
            </Col>
          </Row>

          {/* Header lista */}
          <Row className="g-3 mt-2 mb-1">
            <Col md={3}>
              <strong>Categoria</strong>
            </Col>
            <Col md={4}>
              <strong>Email</strong>
            </Col>
            <Col md={3}>
              <strong>Password</strong>
            </Col>
            <Col md={2} >
              <strong>Azioni</strong>
            </Col>
          </Row>
          <hr className="mt-0" />

          {rows.map(row => (
            <Row key={row.ID} className="g-3 align-items-center mb-3">
              <Col md={3}>
                <Input
                  type="select"
                  value={row.categoryID}
                  onChange={e =>
                    setRows(prev =>
                      prev.map(r =>
                        r.ID === row.ID
                          ? { ...r, categoryID: Number(e.target.value) }
                          : r
                      )
                    )
                  }
                >
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={4}>
                <Input
                  type="text"
                  value={row.email}
                  onChange={e =>
                    setRows(prev =>
                      prev.map(r =>
                        r.ID === row.ID ? { ...r, email: e.target.value } : r
                      )
                    )
                  }
                />
              </Col>

              <Col md={3}>
                <Input
                  type="password"
                  value={row.password}
                  onChange={e =>
                    setRows(prev =>
                      prev.map(r =>
                        r.ID === row.ID
                          ? { ...r, password: e.target.value }
                          : r
                      )
                    )
                  }
                />
              </Col>

              <Col md={2} className="d-flex  gap-2">
                <Btn
                  color="info"
                  className="text-white"
                  onClick={() => handleUpdate(row)}
                  disabled={savingRow === row.ID}
                >
                  Aggiorna
                </Btn>
                <Btn
                  color="danger"
                  className="text-white"
                  onClick={() => handleDelete(row)}
                  disabled={deletingRow === row.ID}
                >
                  Elimina
                </Btn>
              </Col>
            </Row>
          ))}

          {/* (opzionale) legenda rapida categoria selezionata */}
          {rows.length > 0 && categories.length > 0 && (
            <div className="text-muted small mt-2">
              {rows.slice(0, 1).map(r => (
                <span key={r.ID}>
                  Categoria selezionata: {categoryMap.get(r.categoryID) ?? r.categoryID}
                </span>
              ))}
            </div>
          )}
        </AccordionBody>
      </AccordionItem>
    </Accordion>
  );
};

function CardLike({ children }: { children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-body">{children}</div>
    </div>
  );
}

export default Emails;
